import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../components/SEO';

const Faq = () => {
  const [openIndex, setOpenIndex] = React.useState(null);

  const faqData = [
    {
      question: "What is a multi utility connection?",
      answer: "A multi utility connection is the installation of more than one utility service, typically gas, water and electricity, to a single site under one co ordinated project. Rather than appointing separate contractors and running separate applications for each utility, one provider manages the design, applications, excavation and installation together. This normally reduces the number of site visits, shortens the overall programme and avoids the same ground being opened more than once."
    },
    {
      question: "Who can install a new commercial gas connection in the UK?",
      answer: "Work on gas infrastructure must be carried out by a contractor registered under the Gas Industry Registration Scheme, known as GIRS, which covers the design, construction and commissioning of gas networks. Work involving gas meters and appliances additionally requires Gas Safe registration. Green Frog Utilities holds both GIRS registration and Gas Safe registration."
    },
    {
      question: "What is an Independent Connections Provider?",
      answer: "An Independent Connections Provider, or ICP, is an accredited contractor able to design and build utility connections as an alternative to the regional network operator. ICPs are accredited under separate schemes for each utility: GIRS for gas, WIRS for water and NERS for electricity. Using an ICP often gives a client more control over programme and cost than going directly to the incumbent operator. Green Frog Utilities is registered under all three schemes."
    },
    {
      question: "Can one contractor handle gas, water and electricity connections together?",
      answer: "Yes, provided the contractor holds the relevant accreditation for each utility. Green Frog Utilities is registered under GIRS for gas, WIRS for water and NERS for electricity, which allows all three to be designed and installed under one co ordinated programme with a single point of contact."
    },
    {
      question: "How long does a new commercial gas connection take?",
      answer: "Timescales depend on the capacity required, the distance from the site to the nearest suitable main, whether traffic management or a road closure is needed, and the response times of the relevant network operator. A straightforward commercial connection can often be completed within weeks of design approval, while larger industrial schemes that require reinforcement of the existing network can run to several months. A site survey at the outset gives the most reliable indication."
    },
    {
      question: "What is the difference between a DNO and an IDNO?",
      answer: "A Distribution Network Operator, or DNO, is the regional company that owns and operates the existing electricity distribution network in a given area. An Independent Distribution Network Operator, or IDNO, owns and operates newer networks that connect into the DNO network, most commonly on new developments. A connection can be delivered through either route, and the choice affects both the connection cost and who owns the assets afterwards."
    },
    {
      question: "Do I need a new connection or an upgrade to my existing supply?",
      answer: "If the site has no existing supply, a new connection is required. If a supply already exists but cannot deliver the capacity now needed, for example following a change of use, an extension or the installation of new plant, then an upgrade or reinforcement of the existing service may be sufficient and is usually the quicker route. A site survey establishes which applies."
    },
    {
      question: "What is AMR gas metering?",
      answer: "AMR stands for Automated Meter Reading. An AMR gas meter records consumption and transmits readings remotely, which removes the need for manual meter reads and puts an end to estimated billing. For commercial and industrial sites it provides visibility of consumption patterns, supports accurate billing, and allows unexpected usage to be identified quickly rather than at the end of a billing period."
    },
    {
      question: "What do MAM and MAP mean in gas metering?",
      answer: "MAM stands for Meter Asset Manager, the party responsible for installing, maintaining and managing a gas meter. MAP stands for Meter Asset Provider, the party that owns and funds the meter itself. A provider offering both removes the need to appoint two separate companies, which simplifies the contractual position and can shorten lead times. Green Frog Utilities offers full MAM and MAP services."
    },
    {
      question: "What is a utility diversion, and when is one needed?",
      answer: "A diversion moves an existing utility service away from a proposed building footprint or construction area so that work can proceed safely. It is commonly required when a development is planned over the route of an existing main. A disconnection, by contrast, permanently removes a redundant supply from a site and is often required before demolition. Both must be carried out by an accredited contractor and generally require agreement from the network operator."
    },
    {
      question: "Which areas of the UK does Green Frog Utilities cover?",
      answer: "Green Frog Utilities works across the United Kingdom, operating from its office in Tamworth, Staffordshire."
    },
    {
      question: "How do I get a quotation for a utility connection?",
      answer: "The most useful starting point is the site address, the utility or utilities required, and where known the capacity or load needed. From that a design and quotation can be prepared, usually following a site survey. Green Frog Utilities can be contacted on 01675 488644 or through the enquiry form on the contact page."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div>
      <SEO 
        title="Utility Connection FAQs | Gas, Water and Electrical | Green Frog Utilities"
        description="Answers to common questions on commercial gas, water and electrical connections, multi utility projects, AMR metering and MAM and MAP services in the UK."
        canonical="/faq"
        jsonLd={[faqSchema]}
      />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-emerald-600 to-emerald-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="flex justify-center mb-6">
              <HelpCircle className="h-16 w-16" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Utility Connections, Frequently Asked Questions
            </h1>
            <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
              Find answers to common questions about commercial gas, water and electrical connections, multi utility projects, AMR metering and MAM and MAP services.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div 
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  {openIndex === index ? (
                    <ChevronUp className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  )}
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-6 bg-white">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-2xl p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Still have questions?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Contact our team for expert advice on your utility connection requirements.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  Get in Touch
                </Button>
              </Link>
              <a href="tel:+441675488644">
                <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                  Call 01675 488644
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Faq;
